import {RestService} from "@/services/rest/RestService";
import {CacheService} from "@/services/cache/CacheService";
import {CacheKeyEnum} from "@/services/cache/CacheKeyEnum";
import {CustomerContext} from "@/context/CustomerContext";
import {ImageAPIApi} from "@/api/pharma-photo-product-image";
import {
    ManyProductImagesRequestRestDto,
    ProductImageCreateRestDto,
    ProductImageResultsRestDto,
} from "@/api/pharma-photo-product-image/models";
import {trimLeadingZeroes} from "@/helpers/functions/number";
import {Pp2ProductOverviewRestDto} from "@/api/pharma-pim-pp2/models";
import {LaboProductOverviewRestDto} from "@/api/pharma-pim-labo/models";
import {SelectableProductRestDto} from "@/api/pharma-cpc-mgmt/models";
import {LanguageModeEnum} from "@/api/enums/language-mode-enum";
import {
    CatalogProductWithImageOverviewRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/CatalogProductWithImageOverviewRestDtoModel";
import {
    CustomerProductWithImageOverviewRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/CustomerProductWithImageOverviewRestDtoModel";

export class PhotoRestService extends RestService {

    private imageApiService: ImageAPIApi;

    private static readonly NOT_FOUND_URL: string = 'https://cdn.dp2.digital-pharma.eu/assets/img/blancoverpakking.jpg';

    protected constructor() {
        super();

        this.imageApiService = new ImageAPIApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().photoUrl);
    }

    public getNotFoundUrl(): string {
        return PhotoRestService.NOT_FOUND_URL;
    }

    public clearProductImageCache(cnkCode: string | null, productCode: string | null, language: string): void {
        const customerCode: string = CustomerContext.getCustomerCode();
        const cacheKeyPrefix: string = CacheKeyEnum.PRODUCT_IMAGE_URL
            + '.' + (language ?? '--')
            + '.';

        let codeToClear: string | null = cnkCode ? trimLeadingZeroes(cnkCode) : null;

        if (codeToClear) {
            CacheService.getInstance().invalidate(cacheKeyPrefix + codeToClear, customerCode);
        }

        codeToClear = productCode;

        if (codeToClear) {
            CacheService.getInstance().invalidate(cacheKeyPrefix + codeToClear, customerCode);
        }
    }

    public async getProductImageUrl(cnkCode: number | null,
                                    productCode: string | null,
                                    language: string): Promise<string> {
        const codes: string[] = [];
        if (cnkCode) {
            codes.push(trimLeadingZeroes('' + cnkCode));
        } else {
            codes.push(productCode!);
        }
        const productImagesMap = await this.getImageUrls(codes, language);

        if (cnkCode) {
            return productImagesMap.get(trimLeadingZeroes('' + cnkCode))!;
        }

        return productImagesMap.get(productCode!)!;
    }

    public async fillProductImageUrls(
        products: CatalogProductWithImageOverviewRestDtoModel[],
        language: string
    ): Promise<void> {
        const codes: string[] = products
            .map((product) => product.product.product_info.cnk_code
                ? trimLeadingZeroes(product.product.product_info.cnk_code)
                : product.product.product_code);

        const productImagesMap: Map<string, string> = await this.getImageUrls(codes, language);

        products.forEach((product) => {
                if (product.product.product_info.cnk_code) {
                    product.main_image_url = productImagesMap.get(trimLeadingZeroes(product.product.product_info.cnk_code));
                } else {
                    product.main_image_url = productImagesMap.get(product.product.product_code);
                }
            });
    }

    public async fillCustomerProductImageUrls(products: CustomerProductWithImageOverviewRestDtoModel[], language: string): Promise<void> {
        const codes: string[] = products
            .map((product) => product.product.cnk_code
                ? trimLeadingZeroes(product.product.cnk_code)
                : product.product.product_code);

        const productImagesMap: Map<string, string> = await this.getImageUrls(codes, language);

        products.forEach((product) => {
                if (product.product.cnk_code) {
                    product.main_image_url = productImagesMap.get(trimLeadingZeroes(product.product.cnk_code));
                } else {
                    product.main_image_url = productImagesMap.get(product.product.product_code);
                }
            });
    }

    public async fillPimProductImageUrls(products: Pp2ProductOverviewRestDto[]): Promise<void> {
        const cnkCodes: string[] = products
            .filter((product) => product.product_info.cnk_code)
            .map((product) => '' + product.product_info.cnk_code);

        let cnkCodeMap: Map<string, string> = await this.getImageUrls(cnkCodes, 'nl');

        products
            .filter((product) => product.product_info.cnk_code)
            .forEach((product) => {
                if (!product.product_info.main_image_url) {
                    product.product_info.main_image_url = {};
                }
                product.product_info.main_image_url.nl = cnkCodeMap.get('' + product.product_info.cnk_code);
            });

        cnkCodeMap = await this.getImageUrls(cnkCodes, 'fr');

        products
            .filter((product) => product.product_info.cnk_code)
            .forEach((product) => {
                if (!product.product_info.main_image_url) {
                    product.product_info.main_image_url = {};
                }
                product.product_info.main_image_url.fr = cnkCodeMap.get('' + product.product_info.cnk_code);
            });

        cnkCodeMap = await this.getImageUrls(cnkCodes, 'de');

        products
            .filter((product) => product.product_info.cnk_code)
            .forEach((product) => {
                if (!product.product_info.main_image_url) {
                    product.product_info.main_image_url = {};
                }
                product.product_info.main_image_url.de = cnkCodeMap.get('' + product.product_info.cnk_code);
            });

        cnkCodeMap = await this.getImageUrls(cnkCodes, 'en');

        products
            .filter((product) => product.product_info.cnk_code)
            .forEach((product) => {
                if (!product.product_info.main_image_url) {
                    product.product_info.main_image_url = {};
                }
                product.product_info.main_image_url.en = cnkCodeMap.get('' + product.product_info.cnk_code);
            });
    }

    public async fillLaboProductImageUrls(
        products: LaboProductOverviewRestDto[],
        language: string
    ): Promise<void> {
        const cnkCodes: string[] = products
            .filter((product) => product.product_info.cnk_code)
            .map((product) => '' + product.product_info.cnk_code);

        const cnkCodeMap: Map<string, string> = await this.getImageUrls(cnkCodes, language);

        products
            .filter((product) => product.product_info.cnk_code)
            .forEach((product) => {
                product.product_info.main_image_url = cnkCodeMap.get('' + product.product_info.cnk_code!);
            });
    }

    public async fillSelectableProductImageUrls(
        products: SelectableProductRestDto[],
        language: string
    ): Promise<void> {
        const codes: string[] = products
            .map((product) => product.cnk_code ? trimLeadingZeroes(product.cnk_code) : product.product_code);

        const codesMap: Map<string, string> = await this.getImageUrls(codes, language);

        products
            .forEach((product) => {
                if (product.cnk_code) {
                    product.main_image_url = codesMap.get(trimLeadingZeroes(product.cnk_code));
                } else {
                    product.main_image_url = codesMap.get(trimLeadingZeroes(product.product_code));
                }
            });
    }

    public async createProductImage(productCode: string, productImage: ProductImageCreateRestDto): Promise<void> {
        await this.imageApiService.createCustomerProductImage(productImage, productCode);
    }

    public async deleteProductImage(productCode: string, originalFilename: string): Promise<void> {
        await this.imageApiService.deleteCustomerProductImage(productCode, originalFilename);
    }

    private async getImageUrls(codes: string[], language: string): Promise<Map<string, string>> {
        const customerCode = CustomerContext.getCustomerCode();
        const cacheKeyPrefix: string = CacheKeyEnum.PRODUCT_IMAGE_URL
            + '.' + (language ?? '--')
            + '.';

        const missingUrlCodes: string[] = [];
        const resultMap: Map<string, string> = new Map();

        codes.forEach(code => {
            const url: string | null = CacheService.getInstance().get(
                cacheKeyPrefix + code,
                customerCode
            );
            if (url) {
                resultMap.set(code, url);
            } else {
                missingUrlCodes.push(code);
            }

        })

        if (!missingUrlCodes.length) {
            return resultMap;
        }

        const manyProductImagesRequestRestDto: ManyProductImagesRequestRestDto = {
            language_mode: LanguageModeEnum.FALLBACK,
            product_codes: missingUrlCodes
        };

        const response = await this.imageApiService.findManyMainProductImage(manyProductImagesRequestRestDto);
        const result: ProductImageResultsRestDto = response.data;
        if (result.results?.length) {
            for (const image of result.results) {
                if (!image.product_code) {
                    continue;
                }

                let url: string = PhotoRestService.NOT_FOUND_URL;
                if (image.images?.length && image.images[0].url) {
                    url = image.images[0].url;
                }

                let code: string = image.product_code;
                if (code.length < 9) {
                    code = trimLeadingZeroes(code);
                }
                CacheService.getInstance().set(
                    cacheKeyPrefix + code,
                    url,
                    customerCode,
                    58 * 60_000 /* URLs are valid for 60 minutes, so cache them for 58 minutes */
                );
                resultMap.set(code, url);
            }
        }

        return resultMap;
    }
}
